import { needsConvertedImagePreview } from '../../utils/convertedImagePreview';
import { spreadsheetPreviewKind } from '../../utils/spreadsheetPreview';

export type InboxAttachmentPreviewTarget = {
  downloadUrl: string;
  filename: string;
  contentType?: string | null;
};

const INBOX_ATTACHMENT_DOWNLOAD_PATH =
  /\/api\/inbox\/accounts\/[^/]+\/attachments\/download$/;

export function buildInboxAttachmentDownloadUrl(params: {
  accountId: string | number;
  messageId: string;
  attachmentId: string;
  filename: string;
  inline?: boolean;
}): string {
  const base = `/api/inbox/accounts/${encodeURIComponent(String(params.accountId))}/attachments/download`;
  const search = new URLSearchParams();
  search.set('message_id', params.messageId);
  search.set('attachment_id', params.attachmentId);
  search.set('filename', params.filename || 'attachment');
  if (params.inline) search.set('inline', '1');
  return `${base}?${search.toString()}`;
}

export function withInboxAttachmentInline(downloadUrl: string): string {
  const trimmed = String(downloadUrl || '').trim();
  if (!trimmed) return trimmed;
  if (/^(blob:|data:)/i.test(trimmed)) return trimmed;
  try {
    const url = new URL(trimmed, 'http://inbox.local');
    url.searchParams.set('inline', '1');
    return `${url.pathname}?${url.searchParams.toString()}`;
  } catch {
    const sep = trimmed.includes('?') ? '&' : '?';
    return `${trimmed}${sep}inline=1`;
  }
}

export function withInboxAttachmentPreview(downloadUrl: string): string {
  const inlineUrl = withInboxAttachmentInline(downloadUrl);
  if (!inlineUrl) return inlineUrl;
  if (/^(blob:|data:)/i.test(inlineUrl)) return inlineUrl;
  try {
    const url = new URL(inlineUrl, 'http://inbox.local');
    url.searchParams.set('preview', '1');
    return `${url.pathname}?${url.searchParams.toString()}`;
  } catch {
    const sep = inlineUrl.includes('?') ? '&' : '?';
    return `${inlineUrl}${sep}preview=1`;
  }
}

export function parseInboxAttachmentDownloadUrl(rawUrl: string): InboxAttachmentPreviewTarget | null {
  const trimmed = String(rawUrl || '').trim();
  if (!trimmed || /^cid:/i.test(trimmed)) return null;

  try {
    const url = new URL(trimmed, 'http://inbox.local');
    if (!INBOX_ATTACHMENT_DOWNLOAD_PATH.test(url.pathname)) return null;

    const messageId = url.searchParams.get('message_id')?.trim();
    const attachmentId = url.searchParams.get('attachment_id')?.trim();
    const filename = url.searchParams.get('filename')?.trim() || 'attachment';
    if (!messageId || !attachmentId) return null;

    return {
      downloadUrl: `${url.pathname}?${url.searchParams.toString()}`,
      filename,
      contentType: null,
    };
  } catch {
    return null;
  }
}

export function isPdfAttachment(filename: string, contentType?: string | null): boolean {
  if (contentType?.toLowerCase().includes('pdf')) return true;
  return filename.toLowerCase().endsWith('.pdf');
}

export function isImageAttachment(filename: string, contentType?: string | null): boolean {
  if (contentType?.toLowerCase().startsWith('image/')) return true;
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'heic', 'heif', 'dng'].includes(ext);
}

export { needsConvertedImagePreview };

export type InboxAttachmentPreviewMode = 'pdf' | 'image' | 'spreadsheet' | 'none';

export type InboxAttachmentVisualKind = 'pdf' | 'image' | 'document' | 'spreadsheet' | 'archive' | 'file';

export type InboxAttachmentVisual = {
  kind: InboxAttachmentVisualKind;
  label: string;
  badgeClassName: string;
  canPreview: boolean;
};

/** Visual metadata for inbox attachment chips. */
export function getInboxAttachmentVisual(
  filename: string,
  contentType?: string | null,
): InboxAttachmentVisual {
  const previewMode = getInboxAttachmentPreviewMode(filename, contentType);
  if (previewMode === 'pdf') {
    return {
      kind: 'pdf',
      label: 'PDF',
      badgeClassName: 'bg-red-50 text-red-700 ring-red-100',
      canPreview: true,
    };
  }
  if (previewMode === 'image') {
    return {
      kind: 'image',
      label: 'Bild',
      badgeClassName: 'bg-violet-50 text-violet-700 ring-violet-100',
      canPreview: true,
    };
  }

  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  if (['doc', 'docx', 'odt', 'rtf', 'txt'].includes(ext)) {
    return {
      kind: 'document',
      label: ext.toUpperCase().slice(0, 4),
      badgeClassName: 'bg-blue-50 text-blue-700 ring-blue-100',
      canPreview: false,
    };
  }
  if (['xls', 'xlsx', 'csv', 'ods'].includes(ext)) {
    return {
      kind: 'spreadsheet',
      label: ext.toUpperCase().slice(0, 4),
      badgeClassName: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
      canPreview: previewMode === 'spreadsheet',
    };
  }
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
    return {
      kind: 'archive',
      label: 'ZIP',
      badgeClassName: 'bg-amber-50 text-amber-800 ring-amber-100',
      canPreview: false,
    };
  }

  return {
    kind: 'file',
    label: ext ? ext.toUpperCase().slice(0, 4) : 'DATEI',
    badgeClassName: 'bg-app-surface-muted text-app-text-muted ring-slate-200',
    canPreview: false,
  };
}

/** Pick a single preview mode when MIME type and filename disagree. */
export function getInboxAttachmentPreviewMode(
  filename: string,
  contentType?: string | null,
): InboxAttachmentPreviewMode {
  if (isPdfAttachment(filename, contentType)) return 'pdf';
  if (isImageAttachment(filename, contentType)) return 'image';
  if (spreadsheetPreviewKind(filename, contentType)) return 'spreadsheet';
  return 'none';
}

export async function fetchInboxAttachmentBlob(downloadUrl: string, filename?: string): Promise<Blob> {
  const resp = await fetch(downloadUrl, { method: 'GET', credentials: 'same-origin' });
  if (!resp.ok) {
    let msg = `Anhang konnte nicht geladen werden (${resp.status}).`;
    try {
      const data = await resp.json();
      if (data?.error) msg = String(data.error);
    } catch {
      const txt = (await resp.text()).trim();
      if (txt) msg = txt.slice(0, 220);
    }
    throw new Error(msg);
  }

  const blob = await resp.blob();
  const ct = (resp.headers.get('content-type') || blob.type || '').toLowerCase();
  const name = filename || 'attachment';
  const isHtmlFile = /\.html?$/i.test(name);
  if (ct.includes('text/html') && !isHtmlFile) {
    const sample = (await blob.slice(0, 500).text()).toLowerCase();
    if (
      sample.includes('<html') ||
      sample.includes('<!doctype') ||
      sample.includes('datei ist auf der website nicht verfügbar') ||
      sample.includes('file not available')
    ) {
      throw new Error('Anhang konnte nicht geladen werden (Upstream lieferte Fehlerseite).');
    }
  }
  return blob;
}

export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
